import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, canSeeCost, isAdmin } from "@/lib/format";
import {
  LayoutDashboard, Hammer, CheckCircle2, Activity, KanbanSquare, Clock, Timer, ShoppingCart,
  ReceiptText, FileText, Settings, ScrollText, LogOut, Menu, X, HardHat, FileSignature, BarChart3,
  Plane, UserCircle, BadgeCheck, Inbox, CalendarDays, CalendarRange, Users, AlertOctagon, GraduationCap, Building2,
} from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";

function ImpersonationBanner({ user }) {
  if (!user.impersonated_by) return null;
  const stop = async () => { await api.post("/auth/stop-impersonate"); window.location.href = "/settings"; };
  return (
    <div className="sticky top-14 lg:top-0 z-30 flex flex-wrap items-center justify-between gap-2 px-4 py-2 bg-sky-600 text-white text-sm" data-testid="impersonation-banner">
      <span>Viewing as <b>{user.name}</b> ({ROLE_LABELS[user.role]}) — you are {user.impersonated_by}</span>
      <button onClick={stop} data-testid="stop-impersonate" className="px-3 py-1 rounded-md bg-white text-sky-700 font-bold text-xs uppercase">Return to my account</button>
    </div>
  );
}

const ALLR = ["shop_floor", "supervisor", "office", "management", "support"];
const HRR = ["office", "supervisor", "management", "support"];
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["supervisor", "office", "management", "support"] },
  { to: "/quotations", label: "Quotations", icon: FileSignature, roles: ["office", "supervisor", "management", "support"] },
  { to: "/customers", label: "Customers", icon: Building2, roles: ["supervisor", "office", "management", "support"] },
  { to: "/jobs", label: "Jobs", icon: Hammer, roles: ["supervisor", "office", "management", "support"] },
  { to: "/live-jobs", label: "Live Jobs", icon: Activity, roles: ["supervisor", "office", "management", "support"] },
  { to: "/jobs/completed", label: "Completed Jobs", icon: CheckCircle2, roles: ["supervisor", "office", "management", "support"] },
  { to: "/board", label: "Production Board", icon: KanbanSquare, roles: ["supervisor", "office", "management", "support", "shop_floor"] },
  { to: "/clock", label: "Clock & Time", icon: Clock, roles: ["shop_floor", "supervisor", "office", "management", "support"] },
  { to: "/time", label: "Time & Labour", icon: Timer, roles: ["supervisor", "office", "management", "support"] },
  { to: "/purchasing", label: "Purchasing", icon: ShoppingCart, roles: ["office", "supervisor", "management", "support"] },
  { to: "/ready-to-invoice", label: "Ready to Invoice", icon: ReceiptText, roles: ["office", "supervisor", "management", "support"] },
  { to: "/invoices", label: "Invoices", icon: FileText, roles: ["office", "supervisor", "management", "support"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["supervisor", "office", "management", "support"] },
  { to: "/settings", label: "Setup", icon: Settings, roles: ["management", "support", "office"] },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, roles: ["supervisor", "office", "management", "support"] },
  { to: "/hr/leave", label: "My Leave", icon: Plane, roles: ALLR, group: "HR" },
  { to: "/hr/profile", label: "My Profile", icon: UserCircle, roles: ALLR, group: "HR" },
  { to: "/hr/certs", label: "My Certs", icon: BadgeCheck, roles: ALLR, group: "HR" },
  { to: "/hr/requests", label: "Leave Requests", icon: Inbox, roles: HRR, group: "HR" },
  { to: "/hr/calendar", label: "Leave Calendar", icon: CalendarRange, roles: HRR, group: "HR" },
  { to: "/hr/whos-off", label: "Who's Off", icon: CalendarDays, roles: ["supervisor", ...HRR], group: "HR" },
  { to: "/hr/people", label: "People", icon: Users, roles: HRR, group: "HR" },
  { to: "/hr/certs-expiring", label: "Certs Expiring", icon: AlertOctagon, roles: HRR, group: "HR" },
  { to: "/hr/apprentices", label: "Apprentices", icon: GraduationCap, roles: HRR, group: "HR" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user.role));

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const SideContent = () => (
    <>
      <div className="px-5 py-6 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <HardHat className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-lg tracking-wide leading-none">MK</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Engineering</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((n, i) => (
          <div key={n.to}>
          {n.group && items[i - 1]?.group !== n.group && (
            <div className="px-3 pt-4 pb-1 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground" data-testid="nav-group-hr">{n.group}</div>
          )}
          <NavLink
            to={n.to}
            end={n.to === "/jobs"}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.to.replace(/^\//, "").replace(/\//g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/15 text-primary border border-primary/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent border border-transparent"
              }`
            }
          >
            <n.icon className="w-4 h-4 shrink-0" />
            {n.label}
          </NavLink>
          </div>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-border">
        <div className="px-3 py-2 mb-2">
          <div className="text-sm font-semibold truncate">{user.name}</div>
          <div className="text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</div>
          {!canSeeCost(user.role) && (
            <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-amber-500/70">Costs hidden</div>
          )}
        </div>
        <ThemeToggle />
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background grain">
      <aside className="hidden lg:flex w-64 flex-col fixed inset-y-0 border-r border-border bg-card/60 backdrop-blur-md z-40">
        <SideContent />
      </aside>

      {/* mobile header */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 flex items-center justify-between px-4 bg-card/90 backdrop-blur-md border-b border-border z-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <HardHat className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-extrabold tracking-wide">MK Engineering</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" className="p-2">
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative w-64 flex flex-col bg-card border-r border-border">
            <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="absolute top-4 right-3 p-1 z-10">
              <X className="w-5 h-5" />
            </button>
            <SideContent />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 lg:ml-64 pt-14 lg:pt-0 relative z-10"><ImpersonationBanner user={user} />{children}</main>
    </div>
  );
}
